import React from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { FiTarget, FiUsers, FiClock, FiStar, FiAward, FiMapPin, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';

const ComparisonView = ({
  period1Data,
  period2Data,
  period1Label,
  period2Label,
  viewMode = 'side-by-side', // 'side-by-side' | 'overlay'
  onViewModeChange,
  selectedMetrics = ['completions', 'visitors'],
  onMetricsChange,
  deltas = {}
}) => {
  const availableMetrics = [
    { key: 'completions', label: 'Completions', icon: FiTarget },
    { key: 'visitors', label: 'Unique Visitors', icon: FiUsers },
    { key: 'peakTimes', label: 'Peak Times', icon: FiClock },
    { key: 'nftRate', label: 'NFT Mint Rate', icon: FiAward }
  ];

  const toggleMetric = (key) => {
    if (selectedMetrics.includes(key)) {
      onMetricsChange(selectedMetrics.filter(m => m !== key));
    } else {
      onMetricsChange([...selectedMetrics, key]);
    }
  };

  // DeltaCard component
  const DeltaCard = ({ label, value1, value2, delta, suffix = '' }) => {
    const isPositive = delta >= 0;
    const TrendIcon = isPositive ? FiTrendingUp : FiTrendingDown;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-600 mb-2">{label}</p>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-2xl font-bold text-gray-900">{value1}{suffix}</p>
            <p className="text-sm text-gray-500">vs {value2}{suffix}</p>
          </div>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
            isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            <TrendIcon className="w-4 h-4" />
            {isPositive ? '+' : ''}{delta}%
          </div>
        </div>
      </div>
    );
  };

  // Calculate shared Y-axis max for side-by-side comparison charts
  const getSharedMax = (data1, data2, key) => {
    const max1 = Math.max(0, ...(data1 || []).map(d => d[key] || 0));
    const max2 = Math.max(0, ...(data2 || []).map(d => d[key] || 0));
    // Add 10% padding and round up to nice number
    const rawMax = Math.max(max1, max2);
    if (rawMax === 0) return 5; // Minimum scale
    const padding = rawMax * 0.1;
    return Math.ceil(rawMax + padding);
  };

  // Shared max values for charts
  const completionsMax = getSharedMax(period1Data?.daily, period2Data?.daily, 'completions');
  const peakHoursMax = getSharedMax(period1Data?.peakHours, period2Data?.peakHours, 'activity');

  // Merge daily data for overlay chart
  const getMergedDailyData = () => {
    const period1Daily = period1Data?.daily || [];
    const period2Daily = period2Data?.daily || [];

    // Create a map of all dates
    const allDates = new Set([
      ...period1Daily.map(d => d.date),
      ...period2Daily.map(d => d.date)
    ]);

    // For overlay, we'll show both periods aligned by index
    const maxLength = Math.max(period1Daily.length, period2Daily.length);
    return Array.from({ length: maxLength }, (_, i) => ({
      index: i + 1,
      [period1Label]: period1Daily[i]?.completions || 0,
      [period2Label]: period2Daily[i]?.completions || 0
    }));
  };

  return (
    <div className="space-y-6">
      {/* View mode toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => onViewModeChange('side-by-side')}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              viewMode === 'side-by-side'
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Side-by-Side
          </button>
          <button
            onClick={() => onViewModeChange('overlay')}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              viewMode === 'overlay'
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Overlay
          </button>
        </div>

        {/* Metric chips */}
        <div className="flex flex-wrap gap-2">
          {availableMetrics.map(m => {
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                onClick={() => toggleMetric(m.key)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                  selectedMetrics.includes(m.key)
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary delta cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DeltaCard
          label="Completions"
          value1={period1Data?.completions || 0}
          value2={period2Data?.completions || 0}
          delta={deltas.completions || 0}
        />
        <DeltaCard
          label="Unique Users"
          value1={period1Data?.uniqueUsers || 0}
          value2={period2Data?.uniqueUsers || 0}
          delta={deltas.uniqueUsers || 0}
        />
        <DeltaCard
          label="NFTs Minted"
          value1={period1Data?.nftsMinted || 0}
          value2={period2Data?.nftsMinted || 0}
          delta={deltas.nftsMinted || 0}
        />
      </div>

      {/* Charts based on view mode */}
      {selectedMetrics.includes('completions') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Completions Over Time</h3>

          {viewMode === 'side-by-side' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Period 1 Chart */}
              <div>
                <p className="text-sm text-gray-600 mb-2 font-medium">{period1Label}</p>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={period1Data?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis type="number" domain={[0, completionsMax]} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="completions" stroke="#11827E" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Period 2 Chart */}
              <div>
                <p className="text-sm text-gray-600 mb-2 font-medium">{period2Label}</p>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={period2Data?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis type="number" domain={[0, completionsMax]} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="completions" stroke="#C53B8A" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            /* Overlay Chart */
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={getMergedDailyData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="index" label={{ value: 'Day', position: 'insideBottom', offset: -5 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey={period1Label} stroke="#11827E" strokeWidth={2} />
                <Line type="monotone" dataKey={period2Label} stroke="#C53B8A" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {selectedMetrics.includes('visitors') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Comparison</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={[
                { metric: 'Unique Users', [period1Label]: period1Data?.uniqueUsers || 0, [period2Label]: period2Data?.uniqueUsers || 0 },
                { metric: 'Total Completions', [period1Label]: period1Data?.completions || 0, [period2Label]: period2Data?.completions || 0 },
                { metric: 'NFTs Minted', [period1Label]: period1Data?.nftsMinted || 0, [period2Label]: period2Data?.nftsMinted || 0 }
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="metric" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey={period1Label} fill="#11827E" />
              <Bar dataKey={period2Label} fill="#C53B8A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {selectedMetrics.includes('peakTimes') && period1Data?.peakHours && period2Data?.peakHours && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Peak Activity Hours</h3>
          {viewMode === 'side-by-side' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-2 font-medium">{period1Label}</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={period1Data.peakHours}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                    <YAxis type="number" domain={[0, peakHoursMax]} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="activity" fill="#11827E" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2 font-medium">{period2Label}</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={period2Data.peakHours}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                    <YAxis type="number" domain={[0, peakHoursMax]} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="activity" fill="#C53B8A" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={period1Data.peakHours.map((h, i) => ({
                  hour: h.hour,
                  [period1Label]: h.activity,
                  [period2Label]: period2Data.peakHours[i]?.activity || 0
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey={period1Label} fill="#11827E" />
                <Bar dataKey={period2Label} fill="#C53B8A" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {selectedMetrics.includes('nftRate') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">NFT Mint Rate</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* NFT Rate Comparison Bar Chart */}
            <div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={[
                    {
                      metric: 'Mint Rate (%)',
                      [period1Label]: period1Data?.nftMintRate || 0,
                      [period2Label]: period2Data?.nftMintRate || 0
                    }
                  ]}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="metric" width={100} />
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend />
                  <Bar dataKey={period1Label} fill="#11827E" barSize={40} />
                  <Bar dataKey={period2Label} fill="#C53B8A" barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* NFT Stats Summary */}
            <div className="flex flex-col justify-center space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
                  <p className="text-sm text-gray-600 font-medium">{period1Label}</p>
                  <p className="text-3xl font-bold text-primary-700">{period1Data?.nftMintRate || 0}%</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {period1Data?.nftsMinted || 0} of {period1Data?.completions || 0} minted
                  </p>
                </div>
                <div className="bg-secondary-50 rounded-lg p-4 border border-secondary-200">
                  <p className="text-sm text-gray-600 font-medium">{period2Label}</p>
                  <p className="text-3xl font-bold text-secondary-700">{period2Data?.nftMintRate || 0}%</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {period2Data?.nftsMinted || 0} of {period2Data?.completions || 0} minted
                  </p>
                </div>
              </div>
              {/* Change indicator */}
              {period2Data?.nftMintRate > 0 && (
                <div className={`text-center p-3 rounded-lg ${
                  (period1Data?.nftMintRate || 0) >= (period2Data?.nftMintRate || 0)
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {(period1Data?.nftMintRate || 0) >= (period2Data?.nftMintRate || 0) ? (
                    <span className="flex items-center justify-center gap-2">
                      <FiTrendingUp className="w-5 h-5" />
                      Mint rate improved by {((period1Data?.nftMintRate || 0) - (period2Data?.nftMintRate || 0)).toFixed(1)} percentage points
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <FiTrendingDown className="w-5 h-5" />
                      Mint rate decreased by {((period2Data?.nftMintRate || 0) - (period1Data?.nftMintRate || 0)).toFixed(1)} percentage points
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonView;
